import { z } from 'zod';
import { labelledUuid } from './common.js';

/**
 * Free-text "why this needs a second pair of eyes" reason. Operators flag
 * vouchers they aren't sure about; the CO resolves with a brief note. Both
 * fields cap at 500 to match the existing narration column convention.
 */
const reasonSchema = z
  .string({ message: 'Reason is required' })
  .min(3, 'Reason must be at least 3 characters')
  .max(500, 'Reason must be at most 500 characters')
  .transform((s) => s.trim());

const resolutionSchema = z
  .string({ message: 'Resolution note is required' })
  .min(3, 'Resolution note must be at least 3 characters')
  .max(500, 'Resolution note must be at most 500 characters')
  .transform((s) => s.trim());

export const flagReviewRequestSchema = z.object({
  transactionId: labelledUuid('transaction'),
  reason: reasonSchema,
});
export type FlagReviewRequest = z.infer<typeof flagReviewRequestSchema>;

export const resolveReviewRequestSchema = z.object({
  resolution: resolutionSchema,
});
export type ResolveReviewRequest = z.infer<typeof resolveReviewRequestSchema>;

const reviewStatusSchema = z.enum(['OPEN', 'RESOLVED', 'ALL']).default('OPEN');

export const reviewListQuerySchema = z.object({
  status: reviewStatusSchema,
  /** Restrict to reviews against transactions in a given fund. */
  fundId: labelledUuid('fund').optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type ReviewListQuery = z.infer<typeof reviewListQuerySchema>;

/**
 * One review-queue entry. Embeds the linked transaction summary so the UI
 * doesn't need a second round-trip per row — the queue is the primary
 * surface for the CO to triage and these summaries are small.
 */
export interface ReviewResponse {
  id: string;
  transactionId: string;
  reason: string;
  raisedAt: string;
  raisedByUserId: string;
  raisedByUsername: string;
  raisedByName: string;
  resolution: string | null;
  resolvedAt: string | null;
  resolvedByUserId: string | null;
  resolvedByUsername: string | null;
  resolvedByName: string | null;
  transaction: {
    id: string;
    fundCode: string;
    subHeadCode: string | null;
    subHeadName: string | null;
    voucherNo: string;
    transactionDate: string;
    direction: 'CREDIT' | 'DEBIT';
    amount: string;
    narration: string;
    enteredByUsername: string;
    enteredByName: string;
  };
}

export interface ReviewOpenCount {
  /** Number of OPEN reviews currently visible to the caller (RBAC-scoped). */
  open: number;
}
